import { XIcon } from '@phosphor-icons/react'
import { useCallback } from 'react'
import { useQuery } from 'urql'
import { InlineAsyncPicker, type PickerOption } from 'app/components/shared/InlineAsyncPicker'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { WORKFLOW_TEST_CONTACTS_QUERY } from 'app/lib/workflows/graphql'

type ContactHit = { id: string; name?: string; cellPhone?: string; homePhone?: string; workPhone?: string }

const CONTACT_FIELDS = `id name cellPhone homePhone workPhone`

/** The number a text would actually go to, which is the whole reason this field exists. */
const contactPhone = (hit: ContactHit) => hit.cellPhone || hit.homePhone || hit.workPhone

/** Find-a-contact field: search by name or phone number, pick, get a clearable chip. Nobody outside
 *  this room has ever seen a raw contact id, so no surface should ask for one. The selected id can
 *  also arrive programmatically (a "use a recent record" prefill), so the name is resolved from the id.
 *
 *  Rows carry the phone number because duplicate contact records are normal in this data and the
 *  callers here SEND: picking the wrong "John Smith" texts a different person (TRK-5694). */
export function ContactSearchField({
  value,
  onChange,
  locationId,
  inputId,
  placeholder = `Search contacts by name or number…`,
}: {
  /** The selected contact id, or empty for none. */
  value: string
  onChange: (id: string, name?: string) => void
  /** Enrollment does not check the contact's location (TRK-5774), so this is the only guard. */
  locationId?: string
  inputId?: string
  placeholder?: string
}) {
  // Resolves the chip's label for an id that arrived from outside this field.
  const [{ data }] = useQuery<{
    contacts: { hits: { id: string; name?: string | null; cellPhone?: string | null }[] }
  }>({
    query: WORKFLOW_TEST_CONTACTS_QUERY,
    variables: { search: `id:${value}`, pagination: { limit: 1 } },
    pause: !value,
  })
  const picked = data?.contacts.hits[0]
  const name = picked?.name ?? undefined
  const pickedPhone = formatPhoneNumber(picked?.cellPhone ?? undefined)

  const fetcher = useCallback(
    async ({ query }: { query: string }): Promise<{ hits: PickerOption[] }> => {
      const raw = query.trim()
      // Contact search hits ES query_string raw, and Lucene specials 500 the request — strip rather
      // than escape, since a name search never needs operators and stripping fails safe. Digits come
      // off the UNSTRIPPED input, so "(570) 575-7175" and "570-575" still read as a number.
      const term = raw.replace(/[+\-!(){}[\]^"~*?:\\/&|<>=]/g, ` `).trim()
      const digits = raw.replace(/\D/g, ``)

      const clauses: string[] = []
      if (term.length >= 2 && /[a-z]/i.test(term)) clauses.push(`name:*${term}*`)
      // Any of the three phone fields, because a contact reached on a home number is still that contact.
      if (digits.length >= 3) {
        clauses.push(`cellPhone:*${digits}* OR homePhone:*${digits}* OR workPhone:*${digits}*`)
      }
      if (clauses.length === 0) return { hits: [] }

      const match = clauses.length === 1 ? clauses[0] : clauses.map((clause) => `(${clause})`).join(` OR `)
      const { hits } = await gqlFetchList<ContactHit>(
        `contacts`,
        {
          search: locationId ? `locationId:${locationId} AND (${match})` : match,
          pagination: { limit: 8 },
        },
        CONTACT_FIELDS,
      )
      return {
        hits: hits.map((hit) => ({
          id: hit.id,
          name: hit.name,
          // Never blank: a row with no second line reads as "same as the one above".
          subtitle: formatPhoneNumber(contactPhone(hit)) ?? `No phone number`,
        })),
      }
    },
    [locationId],
  )

  if (value) {
    return (
      <span className="flex min-w-0 items-center gap-1.5 rounded-md border border-strong bg-surface px-2 py-1 text-xs">
        <span className="min-w-0 truncate">
          <span className="font-medium text-default">{name ?? `…`}</span>
          {/* The number stays visible after picking, so the choice can be checked before Enroll. */}
          {pickedPhone && <span className="ml-1.5 text-subtle">{pickedPhone}</span>}
        </span>
        <button
          type="button"
          aria-label="Clear contact"
          onClick={() => onChange(``)}
          className="ml-auto flex size-5 shrink-0 items-center justify-center rounded text-minimal hover:bg-surface-muted hover:text-default"
        >
          <XIcon size={12} weight="bold" />
        </button>
      </span>
    )
  }

  return (
    <InlineAsyncPicker
      entity="contacts"
      fetcher={fetcher}
      searchPlaceholder={placeholder}
      emptyLabel="No contacts match that name or number"
      triggerClassName="w-full rounded-md border border-strong px-2 py-1.5 text-left text-xs hover:bg-surface-muted"
      display={
        <span id={inputId} className="text-subtle">
          {placeholder}
        </span>
      }
      onSave={(id, pickedName) => onChange(id ?? ``, pickedName)}
    />
  )
}
